/**
 * 发行人项目管理API - RTK Query
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './baseApi';

// 项目基本信息接口
export interface Project {
  id: string;
  name: string;
  issuer_name: string;
  issuer_id: string;
  stage: string;
  status: 'active' | 'pending' | 'completed' | 'suspended' | 'cancelled';
  progress: number;
  issuance_amount: number;
  currency: string;
  term: number;
  interest_rate?: number;
  green_project_type: string;
  environmental_benefit: string;
  created_at: string;
  updated_at: string;
  expected_completion: string;
  project_manager: string;
  exchange_name: string;
  risk_level: 'low' | 'medium' | 'high';
  tags: string[];

  // 问卷状态信息
  questionnaire_status: {
    questionnaire_id: string;
    status: string;
    decision?: string;
    ai_score?: number;
    risk_level?: string;
    submitted_at: string;
    reviewed_at?: string;
    reviewed_by?: string;
    review_comments?: string;
    decision_reason?: string;
    rejection_reasons: string[];
    status_description: string;
    next_actions: string[];
  };
}

// 项目详情接口
export interface ProjectDetail extends Project {
  questionnaire_details: {
    questionnaire_id: string;
    issuer_basic_info: any;
    issuer_rating_scale: any;
    financial_status: any;
    green_project_info: any;
    issuance_plan: any;
    risk_factors: any;
    contact_info: any;
  };

  review_status: {
    status: string;
    decision?: string;
    ai_score?: number;
    risk_level?: string;
    submitted_at: string;
    reviewed_at?: string;
    reviewed_by?: string;
    review_comments?: string;
    decision_reason?: string;
    rejection_reasons: string[];
    status_description: string;
    next_actions: Array<{
      action: string;
      title: string;
      description: string;
      priority: 'high' | 'medium' | 'low';
    }>;
  };

  milestones: Array<{
    title: string;
    status: 'completed' | 'in_progress' | 'pending' | 'locked';
    completed_at?: string;
    description: string;
  }>;
}

// API响应接口
export interface GetProjectsResponse {
  success: boolean;
  data: {
    projects: Project[];
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

export interface GetProjectDetailResponse {
  success: boolean;
  data: {
    project: ProjectDetail;
  };
}

// 请求参数接口
export interface GetProjectsRequest {
  page?: number;
  limit?: number;
  userId?: string;
  language?: string;
}

export interface GetProjectDetailRequest {
  projectId: string;
}

// 创建API slice - 使用统一的基础配置
export const issuerProjectApi = createApi({
  reducerPath: 'issuerProjectApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['IssuerProject'],
  endpoints: builder => ({
    // 获取发行人项目列表
    getIssuerProjects: builder.query<GetProjectsResponse, GetProjectsRequest>({
      query: params => ({
        url: '/issuer/projects',
        params,
      }),
      providesTags: ['IssuerProject'],
    }),

    // 获取特定项目详情
    getIssuerProjectDetail: builder.query<
      GetProjectDetailResponse,
      GetProjectDetailRequest
    >({
      query: ({ projectId }) => ({
        url: `/issuer/projects/${projectId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { projectId }) => [
        { type: 'IssuerProject', id: projectId },
      ],
    }),
  }),
});

// 导出hooks
export const { useGetIssuerProjectsQuery, useGetIssuerProjectDetailQuery } =
  issuerProjectApi;
